'use client';

import React, { useEffect, useState } from "react";
import { Loader2, Crown, Trash2 } from "lucide-react";
import { Drawer } from "./Drawer";
import { RolePicker } from "./RolePicker";
import { RoleChip } from "./RoleChip";
import { useTeam, errText, formatPhone, STATUS_OPTIONS } from "../internals";
import { useToast } from "./Toaster";

export const MemberEditDrawer = ({ open, member, onClose, onChanged, onTransfer, onRemove }) => {
  const { storage, currentUser, costume } = useTeam();
  const toast = useToast();

  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [role, setRole] = useState("staff");
  const [status, setStatus] = useState("active");
  const [enquiriesAccess, setEnquiriesAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!member) return;
    setName(member.name || "");
    setDesignation(member.designation || "");
    setRole(member.role || "staff");
    setStatus(member.status || "active");
    setEnquiriesAccess((member.owned_modules || []).includes("enquiries"));
    setErr(null);
  }, [member]);

  if (!member) return null;

  const isAdmin = ["owner", "admin"].includes(currentUser.role);
  const isOwnerActor = currentUser.role === "owner";
  const isSelf =
    (member.user_id && member.user_id === currentUser.id) ||
    (member.phone && currentUser.phone && member.phone === currentUser.phone);

  const canEditIdentity = isSelf || isAdmin;
  const canEditRoleStatus = isAdmin && !isSelf;
  const canRemove = isAdmin && !isSelf;
  const canTransfer = isOwnerActor && !isSelf && member.role !== "owner";

  // Two-role model: only Owner/Admin and Staff are ever assignable. The
  // owner's own role changes only via Transfer ownership.
  const roles = (costume.visibleRoles || ["admin", "staff"]).filter((r) => r !== "owner");

  const save = async () => {
    setErr(null);
    if (name.trim().length === 0) return setErr("Name can't be empty.");
    setSaving(true);
    try {
      const patch = {};
      if (canEditIdentity && name.trim() !== (member.name || "")) patch.name = name.trim();
      if (canEditIdentity && (designation.trim() || "") !== (member.designation || ""))
        patch.designation = designation.trim();
      if (canEditRoleStatus && role !== member.role) patch.role = role;
      if (canEditRoleStatus && status !== member.status) patch.status = status;
      if (Object.keys(patch).length) await storage.updateMember(member.id, patch);

      if (canEditRoleStatus) {
        const want = enquiriesAccess ? ["enquiries"] : [];
        const have = member.owned_modules || [];
        const changed = want.length !== have.length || want.some((m) => !have.includes(m));
        if (changed) await storage.setMemberModules(member.id, want);
      }

      toast.show("Changes saved.", "success");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      const m = errText(e);
      setErr(m);
      toast.show(m, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => !saving && onClose()}
      testId="member-edit-drawer"
      title={member.name || "Member"}
      subtitle={formatPhone(member.phone)}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="member-cancel-btn"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="flex-1 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="member-save-btn"
            onClick={save}
            disabled={saving}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
          >
            {saving && <Loader2 size={15} className="animate-spin" />}
            Save changes
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Identity */}
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">Name</label>
            <input
              data-testid="member-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEditIdentity}
              className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              Job title <span className="font-normal text-zinc-400">(optional)</span>
            </label>
            <input
              data-testid="member-designation-input"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              disabled={!canEditIdentity}
              placeholder="e.g. Sales executive"
              className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
            />
          </div>
        </div>

        {/* Role */}
        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-700">What can they do?</label>
          {canEditRoleStatus && member.role !== "owner" ? (
            <RolePicker roles={roles} value={role} onChange={setRole} />
          ) : (
            <div className="flex items-center gap-2">
              <RoleChip role={member.role} />
              <span className="text-xs text-zinc-400">
                {isSelf
                  ? "You can't change your own role."
                  : member.role === "owner"
                  ? "Ownership changes only via 'Transfer ownership' below."
                  : "Only an admin can change this."}
              </span>
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-700">Working status</label>
          {canEditRoleStatus ? (
            <div className="grid grid-cols-2 gap-2" data-testid="status-picker">
              {STATUS_OPTIONS.map((s) => {
                const selected = status === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`status-option-${s.value}`}
                    onClick={() => setStatus(s.value)}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      selected
                        ? "border-rose-600 bg-rose-600 text-white"
                        : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">{STATUS_OPTIONS.find((s) => s.value === member.status)?.label || "Active"}</p>
          )}
        </div>

        {/* Module access */}
        {canEditRoleStatus && (
          <div>
            <label className="mb-2 block text-sm font-medium text-zinc-700">Access</label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2.5 hover:bg-zinc-50">
              <input
                type="checkbox"
                data-testid="member-access-enquiries"
                checked={enquiriesAccess}
                onChange={(e) => setEnquiriesAccess(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300"
              />
              <span className="text-sm text-zinc-700">Can use Enquiries</span>
            </label>
          </div>
        )}

        {err && (
          <div data-testid="member-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Danger zone */}
        {(canTransfer || canRemove) && (
          <div className="space-y-2 border-t border-zinc-200 pt-5">
            {canTransfer && (
              <button
                type="button"
                data-testid="member-transfer-btn"
                onClick={() => onTransfer && onTransfer(member)}
                className="flex w-full items-center gap-2 rounded-md border border-zinc-300 px-3 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
              >
                <Crown size={15} className="text-amber-500" />
                Hand over ownership to {member.name}
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                data-testid="member-remove-btn"
                onClick={() => onRemove && onRemove(member)}
                className="flex w-full items-center gap-2 rounded-md border border-red-200 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 size={15} />
                Remove from team
              </button>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};
